import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = value.toDate?.() || new Date(value);
    return date.toLocaleDateString('es-PR', { month: 'short', day: 'numeric' });
  } catch (err) {
    return '—';
  }
};

const SellerLeads = () => {
  const auth = getAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [noteForm, setNoteForm] = useState({
    summary: '',
    channel: 'whatsapp',
    nextStep: '',
    schedule: '',
  });
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [notification, setNotification] = useState('');

  useEffect(() => {
    const fetchLeads = async () => {
      const email = auth.currentUser?.email?.toLowerCase();
      const uid = auth.currentUser?.uid || null;
      if (!email && !uid) return;
      setLoading(true);
      setError(null);
      try {
        const leadsRef = collection(db, 'leads');
        const queries = [];
        if (email) {
          queries.push(query(leadsRef, where('assignedToEmail', '==', email)));
        }
        if (uid) {
          queries.push(query(leadsRef, where('assignedToUid', '==', uid)));
        }
        const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
        const map = new Map();
        snapshots.forEach((snap) => {
          snap.forEach((docSnap) => {
            map.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        });
        const data = Array.from(map.values());
        data.sort(
          (a, b) =>
            (b.updatedAt?.toMillis?.() || b.updatedAt?.seconds || 0) -
            (a.updatedAt?.toMillis?.() || a.updatedAt?.seconds || 0)
        );
        setLeads(data);
      } catch (err) {
        console.error('❌ Error listando leads del vendedor:', err);
        setError('No pudimos cargar tus leads. Verifica la conexión.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [auth]);

  const openLeadModal = async (lead) => {
    setSelectedLead(lead);
    setTimeline([]);
    setTimelineLoading(true);
    try {
      const timelineRef = collection(db, 'leads', lead.id, 'timeline');
      const snap = await getDocs(query(timelineRef, orderBy('createdAt', 'desc')));
      const entries = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTimeline(entries);
    } catch (err) {
      console.error('❌ Error cargando timeline del lead:', err);
    } finally {
      setTimelineLoading(false);
    }
  };

  const closeLeadModal = () => {
    setSelectedLead(null);
    setTimeline([]);
    setTimelineLoading(false);
    setNoteForm({
      summary: '',
      channel: 'whatsapp',
      nextStep: '',
      schedule: '',
    });
    setNotification('');
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead?.id || !noteForm.summary.trim()) return;
    try {
      const timelineRef = collection(db, 'leads', selectedLead.id, 'timeline');
      const scheduledAt = noteForm.schedule
        ? Timestamp.fromDate(new Date(noteForm.schedule))
        : null;
      await addDoc(timelineRef, {
        summary: noteForm.summary.trim(),
        channel: noteForm.channel,
        nextStep: noteForm.nextStep.trim() || null,
        remindAt: scheduledAt,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || 'seller',
      });
      setNoteForm({
        summary: '',
        channel: noteForm.channel,
        nextStep: '',
        schedule: '',
      });
      openLeadModal(selectedLead);
      setNotification('Seguimiento registrado.');
      setTimeout(() => setNotification(''), 2500);
    } catch (err) {
      console.error('❌ Error guardando nota del lead:', err);
      setNotification('No pudimos registrar la nota.');
      setTimeout(() => setNotification(''), 2500);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedLead?.id) return;
    setStatusUpdating(true);
    try {
      await updateDoc(doc(db, 'leads', selectedLead.id), {
        status: newStatus,
        pipelineStage: newStatus,
        updatedAt: serverTimestamp(),
        lastContact: serverTimestamp(),
      });
      setSelectedLead((prev) => ({ ...prev, status: newStatus, pipelineStage: newStatus }));
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === selectedLead.id
            ? {
                ...lead,
                status: newStatus,
                pipelineStage: newStatus,
                updatedAt: Timestamp.now(),
              }
            : lead
        )
      );
      setNotification('Estado actualizado.');
      setTimeout(() => setNotification(''), 2500);
    } catch (err) {
      console.error('❌ Error cambiando estado del lead:', err);
      setNotification('No pudimos actualizar el estado.');
      setTimeout(() => setNotification(''), 2500);
    } finally {
      setStatusUpdating(false);
    }
  };

  const statusOptions = [
    { value: 'nuevo', label: 'Nuevo' },
    { value: 'contactado', label: 'Contactado' },
    { value: 'en_proceso', label: 'En proceso' },
    { value: 'convertido', label: 'Convertido' },
    { value: 'inactivo', label: 'Inactivo' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Mis leads asignados</h2>
        <p className="text-gray-500">
          Solo ves los leads que fueron asignados a tu correo corporativo.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Cargando leads…</p>
      ) : leads.length === 0 ? (
        <p className="text-sm text-gray-500">Aún no tienes leads asignados.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Última actualización</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Notas</th>
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{lead.name || '—'}</p>
                    <p className="text-xs text-gray-500">{lead.email}</p>
                    <p className="text-xs text-gray-500">{lead.phone}</p>
                  </td>
                  <td className="px-4 py-3 capitalize text-gray-700">
                    {(lead.status || 'nuevo').replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(lead.updatedAt || lead.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{lead.aiScore ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {lead.lastContactNote || lead.nextStep || 'Sin notas'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openLeadModal(lead)}
                      className="rounded-full border border-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                    >
                      Gestionar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedLead && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Lead asignado</p>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {selectedLead.name || selectedLead.email}
                </h3>
                <p className="text-sm text-gray-500">{selectedLead.email}</p>
                <p className="text-sm text-gray-500">{selectedLead.phone}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={statusUpdating}
                    onClick={() => handleStatusChange(option.value)}
                    className={`rounded-full px-3 py-1 text-sm ${
                      (selectedLead.status || 'nuevo') === option.value
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={closeLeadModal}
                className="text-sm font-semibold text-gray-400 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>

            {notification && (
              <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                {notification}
              </div>
            )}

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                <h4 className="text-sm font-semibold text-gray-900">Registrar seguimiento</h4>
                <form className="mt-3 space-y-3" onSubmit={handleNoteSubmit}>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Resumen</label>
                    <textarea
                      value={noteForm.summary}
                      onChange={(e) => setNoteForm((prev) => ({ ...prev, summary: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                      rows={3}
                      placeholder="¿Qué hablaste con el cliente?"
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Canal</label>
                      <select
                        value={noteForm.channel}
                        onChange={(e) => setNoteForm((prev) => ({ ...prev, channel: e.target.value }))}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                      >
                        <option value="whatsapp">WhatsApp</option>
                        <option value="telefono">Teléfono</option>
                        <option value="email">Email</option>
                        <option value="visita">Visita</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Recordatorio</label>
                      <input
                        type="datetime-local"
                        value={noteForm.schedule}
                        onChange={(e) => setNoteForm((prev) => ({ ...prev, schedule: e.target.value }))}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Próximo paso</label>
                    <input
                      type="text"
                      value={noteForm.nextStep}
                      onChange={(e) => setNoteForm((prev) => ({ ...prev, nextStep: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                      placeholder="Ej. Enviar propuesta, coordinar visita, etc."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    Guardar seguimiento
                  </button>
                </form>
              </div>

              <div className="rounded-2xl border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900">Historial</h4>
                {timelineLoading ? (
                  <p className="mt-4 text-sm text-gray-500">Cargando notas…</p>
                ) : timeline.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">
                    Todavía no registras actividades para este lead.
                  </p>
                ) : (
                  <ul className="mt-4 space-y-3">
                    {timeline.map((entry) => (
                      <li key={entry.id} className="rounded-2xl bg-gray-50 px-3 py-2 text-sm">
                        <p className="font-semibold text-gray-900">{entry.summary}</p>
                        <p className="text-xs text-gray-500">
                          {entry.channel} ·{' '}
                          {entry.createdAt?.toDate?.().toLocaleString?.('es-PR') || '—'}
                        </p>
                        {entry.nextStep && (
                          <p className="text-xs text-gray-600">Próximo paso: {entry.nextStep}</p>
                        )}
                        {entry.remindAt && (
                          <p className="text-xs text-orange-600">
                            Recordar: {entry.remindAt.toDate?.().toLocaleString?.('es-PR')}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerLeads;
